import { useState, useRef, useContext, useEffect, useCallback } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import type { Editor as TiptapEditor } from '@tiptap/core';

import {
  createUID,
  FileInput,
  Grid,
  Icon,
  Input,
  RadioButton,
  RadioButtonGroup,
  registerIcon,
  useI18n
} from '@pega/cosmos-react-core';
import type { ForwardProps } from '@pega/cosmos-react-core';
import * as pictureIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/picture.icon';

import ToolbarButton from '../../RichTextEditor/Toolbar/ToolbarButton';
import EditorContext from '../Editor.context';
import { StyledFormDialog } from '../Editor.styles';
import useCloseOnEditorClick from '../hooks/useCloseOnEditorClick';
import type { ImageCustomSourceConfig } from '../Editor.types';

registerIcon(pictureIcon);

const escapeHtmlAttribute = (value: string) =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const ImageButton = ({
  editor,
  imageInsertionMode,
  imageCustomSource,
  ...restProps
}: {
  editor: TiptapEditor;
  imageInsertionMode: 'file' | 'url' | 'all';
  imageCustomSource?: ImageCustomSourceConfig;
} & ForwardProps) => {
  const t = useI18n();
  const { addImage } = useContext(EditorContext);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const imagebuttonRef = useRef<HTMLElement>(null);
  const imagepopoverRef = useRef(null);
  const [imageTitle, setImageTitle] = useState('');
  const [imageLinkTitle, setImageLinkTitle] = useState('');
  const [imageActionUrlType, setImageActionUrlType] = useState<'url' | 'field'>('url');
  const [imageActionUrl, setImageActionUrl] = useState('');
  const [incorrectActionUrl, setIncorrectActionUrl] = useState(false);
  const [selectedAttachmentType, setSelectedAttachmentType] = useState(() => {
    if (imageInsertionMode === 'url') {
      return 'link';
    }
    return 'file';
  });
  const [url, setUrl] = useState('');
  const [tempfile, setTempFile] = useState<File>();
  const [incorrectUrl, setIncorrectUrl] = useState(false);
  const [fileEmpty, setFileEmpty] = useState(false);
  const [linkImageActionUrlType, setLinkImageActionUrlType] = useState<'url' | 'field'>('url');
  const [linkImageActionUrl, setLinkImageActionUrl] = useState('');
  const [incorrectLinkImageActionUrl, setIncorrectLinkImageActionUrl] = useState(false);
  const hasImageCustomSource = Boolean(imageCustomSource?.picker && imageCustomSource?.onSubmit);
  const canSubmitCustomSource = imageCustomSource?.canSubmit ?? true;

  useEffect(() => {
    if (imageInsertionMode === 'url') {
      setSelectedAttachmentType('link');
    } else {
      setSelectedAttachmentType('file');
    }
  }, [imageInsertionMode]);

  const onImageInputChange = (addedFiles: File[]) => {
    setTempFile(addedFiles[0]);
    setFileEmpty(false);
  };

  const resetMenu = (reason: 'cancel' | 'apply' = 'cancel') => {
    setUrl('');
    setIncorrectUrl(false);
    setImageLinkTitle('');
    setImageActionUrlType('url');
    setImageActionUrl('');
    setIncorrectActionUrl(false);
    setImageTitle('');
    setTempFile(undefined);
    setSelectedAttachmentType(imageInsertionMode === 'url' ? 'link' : 'file');
    setFileEmpty(false);
    setLinkImageActionUrlType('url');
    setLinkImageActionUrl('');
    setIncorrectLinkImageActionUrl(false);
    imageCustomSource?.onDismissEdit?.(reason);
    setShowImageDialog(false);
  };

  // Close dialog when clicking inside the editor iframe
  const handleEditorClick = useCallback(() => {
    if (showImageDialog) {
      resetMenu();
    }
  }, [showImageDialog]);
  useCloseOnEditorClick(showImageDialog, handleEditorClick);

  const onImageAddSubmit = () => {
    if (selectedAttachmentType === 'file') {
      if (!tempfile) {
        setFileEmpty(true);
        return;
      }
      if (imageActionUrlType === 'url' && incorrectActionUrl) {
        return;
      }
      // Handle field-based action URL for uploaded images
      if (imageActionUrlType === 'field') {
        if (!imageCustomSource?.onSubmit || !canSubmitCustomSource) {
          return;
        }
        const actionUrl = imageCustomSource.onSubmit(imageTitle);
        addImage(tempfile, undefined, imageTitle, actionUrl);
        resetMenu('apply');
        return;
      }
      // Handle static URL action for uploaded images
      if (imageActionUrlType === 'url' && imageActionUrl && !incorrectActionUrl) {
        // Pass action URL to addImage - consumer will handle wrapping with anchor
        addImage(tempfile, undefined, imageTitle, imageActionUrl);
        resetMenu('apply');
        return;
      }
      // No action URL - just add image
      addImage(tempfile, undefined, imageTitle);
      resetMenu('apply');
    } else if (selectedAttachmentType === 'link') {
      if (!url || incorrectUrl) {
        setIncorrectUrl(true);
        return;
      }
      if (hasImageCustomSource && linkImageActionUrlType === 'url' && incorrectLinkImageActionUrl) {
        return;
      }

      const imageUid = createUID();
      let insertHtml = '';
      const imageAltText = imageLinkTitle || t('rte_broken_image');

      if (hasImageCustomSource && linkImageActionUrlType === 'field') {
        if (!canSubmitCustomSource) {
          return;
        }
        const fallbackHref = imageCustomSource?.onSubmit
          ? imageCustomSource.onSubmit(imageAltText)
          : undefined;
        const actionUrl = fallbackHref ? escapeHtmlAttribute(fallbackHref) : undefined;
        if (actionUrl) {
          insertHtml = `<a href="${actionUrl}" target="_blank" rel="noopener noreferrer"><img src="${escapeHtmlAttribute(url)}" data-id="${imageUid}" alt="${escapeHtmlAttribute(imageAltText)}" /></a>`;
        } else {
          insertHtml = `<img src="${escapeHtmlAttribute(url)}" data-id="${imageUid}" alt="${escapeHtmlAttribute(imageAltText)}" />`;
        }
      } else if (
        hasImageCustomSource &&
        linkImageActionUrlType === 'url' &&
        linkImageActionUrl &&
        !incorrectLinkImageActionUrl
      ) {
        const actionUrl = escapeHtmlAttribute(linkImageActionUrl);
        insertHtml = `<a href="${actionUrl}" target="_blank" rel="noopener noreferrer"><img src="${escapeHtmlAttribute(url)}" data-id="${imageUid}" alt="${escapeHtmlAttribute(imageAltText)}" /></a>`;
      } else {
        insertHtml = `<img src="${escapeHtmlAttribute(url)}" data-id="${imageUid}" alt="${escapeHtmlAttribute(imageAltText)}" />`;
      }

      editor.chain().focus().insertContent(insertHtml).run();

      // Add error handler for broken images if no alt text was provided
      if (!imageLinkTitle) {
        // Use the editor's document to target the correct iframe
        const editorDoc = editor.view.dom.ownerDocument;
        requestAnimationFrame(() => {
          const imageEl = editorDoc.querySelector<HTMLImageElement>(`img[data-id="${imageUid}"]`);
          if (imageEl) {
            imageEl.onerror = () => {
              imageEl.alt = t('rte_broken_image');
            };
          }
        });
      }
      resetMenu('apply');
    }
  };

  const onImageAddCancel = () => {
    resetMenu();
  };

  return (
    <>
      <ToolbarButton
        ref={imagebuttonRef}
        onClick={() => {
          setShowImageDialog(true);
        }}
        onKeyDown={(e: ReactKeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setShowImageDialog(true);
          }
        }}
        tooltip={t('rte_image')}
        label={t('rte_image')}
        {...restProps}
      >
        <Icon name='picture' />
      </ToolbarButton>
      {showImageDialog && imagebuttonRef.current && (
        <form>
          <StyledFormDialog
            heading={t('rte_add_image')}
            onSubmit={onImageAddSubmit}
            onCancel={onImageAddCancel}
            onDismiss={() => {
              resetMenu();
            }}
            target={imagebuttonRef.current}
            ref={imagepopoverRef}
          >
            <Grid
              container={{
                gap: 2
              }}
            >
              {imageInsertionMode === 'all' && (
                <Grid container={{ rowGap: 2 }}>
                  <RadioButtonGroup
                    name='chooseListView'
                    label={t('rte_image_source')}
                    onChange={e => {
                      setSelectedAttachmentType(e.target.value);
                    }}
                    data-testid='chooseListView'
                    inline
                    required
                  >
                    <RadioButton
                      id='file'
                      label={t('rte_image_file')}
                      value='file'
                      checked={selectedAttachmentType === 'file'}
                    />
                    <RadioButton
                      id='link'
                      label={t('rte_image_link')}
                      value='link'
                      checked={selectedAttachmentType === 'link'}
                    />
                  </RadioButtonGroup>
                </Grid>
              )}
              {selectedAttachmentType === 'file' && (
                <Grid container={{ rowGap: 2 }}>
                  <FileInput
                    multiple={false}
                    label={t('rte_image_file')}
                    onFilesAdded={onImageInputChange}
                    required
                    accept='image/*'
                    info={fileEmpty ? t('rte_empty_image') : ''}
                    status={fileEmpty ? 'error' : undefined}
                    files={
                      tempfile
                        ? [
                            {
                              name: tempfile.name,
                              id: createUID(),
                              thumbnail: URL.createObjectURL(tempfile),
                              onDelete: () => {
                                setTempFile(undefined);
                              }
                            }
                          ]
                        : []
                    }
                  />
                  <Input
                    label={t('description')}
                    value={imageTitle}
                    onChange={e => {
                      setImageTitle(e.target.value);
                    }}
                    info={t('rte_image_description_info')}
                  />
                  {hasImageCustomSource && (
                    <RadioButtonGroup
                      name='imageActionUrlType'
                      label={t('rte_image_action_source')}
                      onChange={e => {
                        const { value } = e.target;
                        if (value === 'url' || value === 'field') {
                          setImageActionUrlType(value);
                        }
                      }}
                      inline
                    >
                      <RadioButton
                        label={t('rte_link_url')}
                        value='url'
                        checked={imageActionUrlType === 'url'}
                      />
                      <RadioButton
                        label={t('rte_link_field')}
                        value='field'
                        checked={imageActionUrlType === 'field'}
                      />
                    </RadioButtonGroup>
                  )}
                  {hasImageCustomSource && imageActionUrlType === 'url' && (
                    <Input
                      label={t('rte_image_url')}
                      value={imageActionUrl}
                      info={incorrectActionUrl ? t('rte_invalid_url') : ''}
                      status={incorrectActionUrl ? 'error' : undefined}
                      onChange={e => {
                        const urlInput = e.target.value;
                        setImageActionUrl(urlInput);
                        if (urlInput === '') {
                          setIncorrectActionUrl(false);
                          return;
                        }
                        try {
                          const newUrl = new URL(urlInput);
                          setIncorrectActionUrl(!['http:', 'https:'].includes(newUrl.protocol));
                        } catch {
                          setIncorrectActionUrl(true);
                        }
                      }}
                    />
                  )}
                  {hasImageCustomSource &&
                    imageActionUrlType === 'field' &&
                    imageCustomSource?.picker}
                </Grid>
              )}
              {selectedAttachmentType === 'link' && (
                <Grid container={{ rowGap: 2 }}>
                  <Input
                    label={t('rte_image_url')}
                    value={url}
                    required
                    info={incorrectUrl ? t('rte_invalid_url') : ''}
                    status={incorrectUrl ? 'error' : undefined}
                    onChange={e => {
                      const urlInput = e.target.value;
                      setUrl(urlInput);
                      try {
                        const newUrl = new URL(urlInput);
                        setIncorrectUrl(!['http:', 'https:'].includes(newUrl.protocol));
                      } catch {
                        setIncorrectUrl(true);
                      }
                    }}
                  />
                  <Input
                    label={t('description')}
                    value={imageLinkTitle}
                    onChange={e => {
                      setImageLinkTitle(e.target.value);
                    }}
                    info={t('rte_image_description_info')}
                  />
                  {hasImageCustomSource && (
                    <RadioButtonGroup
                      name='linkImageActionUrlType'
                      label={t('rte_image_action_source')}
                      onChange={e => {
                        const { value } = e.target;
                        if (value === 'url' || value === 'field') {
                          setLinkImageActionUrlType(value);
                        }
                      }}
                      inline
                    >
                      <RadioButton
                        label={t('rte_link_url')}
                        value='url'
                        checked={linkImageActionUrlType === 'url'}
                      />
                      <RadioButton
                        label={t('rte_link_field')}
                        value='field'
                        checked={linkImageActionUrlType === 'field'}
                      />
                    </RadioButtonGroup>
                  )}
                  {hasImageCustomSource && linkImageActionUrlType === 'url' && (
                    <Input
                      label={t('rte_image_url')}
                      value={linkImageActionUrl}
                      info={incorrectLinkImageActionUrl ? t('rte_invalid_url') : ''}
                      status={incorrectLinkImageActionUrl ? 'error' : undefined}
                      onChange={e => {
                        const urlInput = e.target.value;
                        setLinkImageActionUrl(urlInput);
                        if (urlInput === '') {
                          setIncorrectLinkImageActionUrl(false);
                          return;
                        }
                        try {
                          const newUrl = new URL(urlInput);
                          setIncorrectLinkImageActionUrl(
                            !['http:', 'https:'].includes(newUrl.protocol)
                          );
                        } catch {
                          setIncorrectLinkImageActionUrl(true);
                        }
                      }}
                    />
                  )}
                  {hasImageCustomSource &&
                    linkImageActionUrlType === 'field' &&
                    imageCustomSource?.picker}
                </Grid>
              )}
            </Grid>
          </StyledFormDialog>
        </form>
      )}
    </>
  );
};

export default ImageButton;
